/**
 * frontend/src/components/topology/TopologyViewer.jsx
 * -----------------------------------------------------
 * Visualiseur de topologie GNS3 basé sur D3 force-directed.
 *
 * Données attendues (depuis GET /api/v1/gns3/reservations/:id/topology) :
 *   { nodes: [...], links: [...] }
 *
 * Format nœud GNS3 :
 *   { node_id, name, node_type, symbol, x, y, console_type, console_port, status }
 *
 * Format lien GNS3 :
 *   { link_id, nodes: [{node_id, label: {text}}, {node_id, label: {text}}] }
 *
 * Fonctionnalités :
 *   - Disposition initiale respectant les coordonnées x/y du fichier .gns3
 *   - Drag & drop des nœuds
 *   - Zoom / pan (molette + glisser)
 *   - Labels d'interface sur les liens (ex: "1/6" — "0/0")
 *   - Couleur de statut sur les nœuds (started=vert, stopped=rouge, ?)
 *   - Clic sur un nœud → callback onNodeClick(node)
 *   - Tooltip au survol
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as d3 from "d3";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

const NODE_R   = 22;   // rayon nœud
const FONT_SZ  = 11;
const SVG_H    = 500;
const LINK_LABEL_FONT = 9;

// Couleurs statut nœud
const STATUS_FILL = {
  started:  "#22c55e",
  running:  "#22c55e",
  stopped:  "#ef4444",
  suspended:"#f59e0b",
};
const DEFAULT_FILL = "#6b7280";

// Icônes emoji selon node_type (fallback si pas de SVG externe)
const TYPE_ICON = {
  dynamips:   "🖥",
  vpcs:       "💻",
  cloud:      "☁️",
  ethernet_switch: "🔀",
  frame_relay_switch: "🔀",
  atm_switch: "🔀",
  docker:     "🐳",
  qemu:       "🖥",
  iou:        "🖥",
};

function nodeIcon(node) {
  if (node.symbol?.includes("switch"))  return "🔀";
  if (node.symbol?.includes("router"))  return "📡";
  if (node.symbol?.includes("computer"))return "💻";
  if (node.symbol?.includes("cloud"))   return "☁️";
  if (node.symbol?.includes("firewall"))return "🔥";
  return TYPE_ICON[node.node_type] ?? "📦";
}

function nodeColor(node) {
  const s = String(node.status || "").toLowerCase();
  return STATUS_FILL[s] ?? DEFAULT_FILL;
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

export default function TopologyViewer({
  nodes = [],
  links = [],
  loading = false,
  error = "",
  onNodeClick,
  onRefresh,
}) {
  const svgRef      = useRef(null);
  const containerRef= useRef(null);
  const simRef      = useRef(null);
  const zoomRef     = useRef(null);

  const [tooltip, setTooltip] = useState(null); // {x, y, node}

  // ---------------------------------------------------------------------------
  // Données D3 — on stabilise les références pour le drag
  // ---------------------------------------------------------------------------
  const d3Nodes = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        id: n.node_id,
        // Positions initiales depuis le fichier .gns3 (échelle 1:1)
        x: typeof n.x === "number" ? n.x : 0,
        y: typeof n.y === "number" ? n.y : 0,
      })),
    [JSON.stringify(nodes.map((n) => n.node_id))]
  );

  const d3Links = useMemo(
    () =>
      links.map((l) => ({
        ...l,
        id: l.link_id,
        source: l.nodes?.[0]?.node_id ?? "",
        target: l.nodes?.[1]?.node_id ?? "",
        labelA:  l.nodes?.[0]?.label?.text ?? "",
        labelB:  l.nodes?.[1]?.label?.text ?? "",
      })),
    [JSON.stringify(links.map((l) => l.link_id))]
  );

  // ---------------------------------------------------------------------------
  // Rendu D3
  // ---------------------------------------------------------------------------
  useLayoutEffect(() => {
    if (!svgRef.current || d3Nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width  = svgRef.current.clientWidth  || 800;
    const height = SVG_H;

    // Centrage des coordonnées GNS3 (qui peuvent être négatives)
    const xs = d3Nodes.map((n) => n.x);
    const ys = d3Nodes.map((n) => n.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;

    // Groupe principal (pour zoom/pan)
    const g = svg.append("g").attr("class", "main");

    // Zoom
    const zoom = d3.zoom()
      .scaleExtent([0.2, 4])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
      });
    zoomRef.current = zoom;
    svg.call(zoom);

    // Centrage initial
    const initTranslate = [
      width  / 2 - cx,
      height / 2 - cy,
    ];
    svg.call(
      zoom.transform,
      d3.zoomIdentity.translate(initTranslate[0], initTranslate[1])
    );

    // --- Définitions (marqueur flèche - optionnel) ---
    svg.append("defs")
      .append("marker")
      .attr("id", "arrow")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", NODE_R + 8)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#94a3b8");

    // --- Liens ---
    const linkG = g.append("g").attr("class", "links");

    const linkLines = linkG
      .selectAll("line")
      .data(d3Links)
      .enter()
      .append("line")
      .attr("stroke", "#94a3b8")
      .attr("stroke-width", 1.8)
      .attr("stroke-opacity", 0.7);

    // Labels d'interface sur les liens
    const linkLabelA = linkG
      .selectAll(".lbl-a")
      .data(d3Links)
      .enter()
      .append("text")
      .attr("class", "lbl-a")
      .attr("font-size", LINK_LABEL_FONT)
      .attr("fill", "#64748b")
      .attr("text-anchor", "middle");

    const linkLabelB = linkG
      .selectAll(".lbl-b")
      .data(d3Links)
      .enter()
      .append("text")
      .attr("class", "lbl-b")
      .attr("font-size", LINK_LABEL_FONT)
      .attr("fill", "#64748b")
      .attr("text-anchor", "middle");

    // --- Nœuds ---
    const nodeG = g.append("g").attr("class", "nodes");

    const nodeGroups = nodeG
      .selectAll("g.node")
      .data(d3Nodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .style("cursor", "pointer")
      .call(
        d3.drag()
          .on("start", (event, d) => {
            if (!event.active) simRef.current?.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on("drag", (event, d) => {
            d.fx = event.x;
            d.fy = event.y;
          })
          .on("end", (event, d) => {
            if (!event.active) simRef.current?.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          })
      )
      .on("click", (event, d) => {
        event.stopPropagation();
        onNodeClick?.(d);
      })
      .on("mouseenter", (event, d) => {
        const rect = svgRef.current.getBoundingClientRect();
        setTooltip({
          x: event.clientX - rect.left + 12,
          y: event.clientY - rect.top  - 8,
          node: d,
        });
      })
      .on("mouseleave", () => setTooltip(null));

    // Cercle de fond
    nodeGroups
      .append("circle")
      .attr("r", NODE_R)
      .attr("fill", (d) => nodeColor(d))
      .attr("fill-opacity", 0.18)
      .attr("stroke", (d) => nodeColor(d))
      .attr("stroke-width", 2);

    // Icône emoji centré
    nodeGroups
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("font-size", 18)
      .attr("y", -1)
      .text((d) => nodeIcon(d));

    // Label nom du nœud en dessous
    nodeGroups
      .append("text")
      .attr("text-anchor", "middle")
      .attr("y", NODE_R + FONT_SZ + 2)
      .attr("font-size", FONT_SZ)
      .attr("fill", "#1e293b")
      .attr("font-weight", "600")
      .text((d) => d.name);

    // --- Simulation force ---
    const simulation = d3
      .forceSimulation(d3Nodes)
      .force(
        "link",
        d3.forceLink(d3Links)
          .id((d) => d.id)
          .distance(120)
          .strength(0.3)
      )
      // Faible répulsion — on respecte les positions .gns3
      .force("charge", d3.forceManyBody().strength(-60))
      // Pas de collision aggressive pour garder le layout fidèle
      .force("collision", d3.forceCollide(NODE_R + 8))
      // Attracteur léger vers les positions d'origine
      .force("x", d3.forceX((d) => d.x).strength(0.8))
      .force("y", d3.forceY((d) => d.y).strength(0.8))
      .alphaDecay(0.05);

    simRef.current = simulation;

    simulation.on("tick", () => {
      linkLines
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => d.target.x)
        .attr("y2", (d) => d.target.y);

      // Labels d'interface : légèrement décalés du centre du lien vers chaque extrémité
      const LABEL_OFFSET = 0.25; // 25% du lien depuis l'origine
      linkLabelA
        .attr("x", (d) => d.source.x + (d.target.x - d.source.x) * LABEL_OFFSET)
        .attr("y", (d) => d.source.y + (d.target.y - d.source.y) * LABEL_OFFSET - 4)
        .text((d) => d.labelA);

      linkLabelB
        .attr("x", (d) => d.target.x + (d.source.x - d.target.x) * LABEL_OFFSET)
        .attr("y", (d) => d.target.y + (d.source.y - d.target.y) * LABEL_OFFSET - 4)
        .text((d) => d.labelB);

      nodeGroups.attr("transform", (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
    };
  }, [d3Nodes, d3Links, onNodeClick]);

  // ---------------------------------------------------------------------------
  // Contrôles zoom
  // ---------------------------------------------------------------------------
  const handleZoomIn  = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().call(zoomRef.current.scaleBy, 1.4);
  }, []);

  const handleZoomOut = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current).transition().call(zoomRef.current.scaleBy, 0.7);
  }, []);

  const handleCenter  = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    d3.select(svgRef.current)
      .transition()
      .call(zoomRef.current.transform, d3.zoomIdentity);
  }, []);

  // ---------------------------------------------------------------------------
  // Rendu JSX
  // ---------------------------------------------------------------------------
  return (
    <Paper sx={{ p: 2, position: "relative" }}>
      {/* En-tête */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontWeight={700}>Topologie</Typography>
          {nodes.length > 0 && (
            <>
              <Chip size="small" label={`${nodes.length} nœuds`} />
              <Chip size="small" label={`${links.length} liens`} variant="outlined" />
            </>
          )}
        </Stack>

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Zoom +">
            <span>
              <ZoomInIcon
                sx={{ cursor: "pointer", opacity: 0.7, "&:hover": { opacity: 1 } }}
                onClick={handleZoomIn}
              />
            </span>
          </Tooltip>
          <Tooltip title="Zoom −">
            <span>
              <ZoomOutIcon
                sx={{ cursor: "pointer", opacity: 0.7, "&:hover": { opacity: 1 } }}
                onClick={handleZoomOut}
              />
            </span>
          </Tooltip>
          <Tooltip title="Centrer">
            <span>
              <CenterFocusStrongIcon
                sx={{ cursor: "pointer", opacity: 0.7, "&:hover": { opacity: 1 } }}
                onClick={handleCenter}
              />
            </span>
          </Tooltip>
          {onRefresh && (
            <Tooltip title="Rafraîchir">
              <span>
                <RefreshIcon
                  sx={{ cursor: "pointer", opacity: 0.7, "&:hover": { opacity: 1 } }}
                  onClick={onRefresh}
                />
              </span>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* Erreur */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* SVG conteneur */}
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          width: "100%",
          height: SVG_H,
          bgcolor: "#f8fafc",
          borderRadius: 1,
          border: "1px solid rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        {/* Overlay chargement */}
        {loading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.75)",
              zIndex: 10,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="caption" sx={{ mt: 1 }}>
              Chargement topologie…
            </Typography>
          </Stack>
        )}

        {/* Placeholder si vide */}
        {!loading && nodes.length === 0 && !error && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ height: "100%", opacity: 0.4 }}
          >
            <Typography>Aucun nœud — démarrez la réservation.</Typography>
          </Stack>
        )}

        <svg
          ref={svgRef}
          width="100%"
          height={SVG_H}
          style={{ display: "block" }}
        />

        {/* Tooltip */}
        {tooltip && (
          <Box
            sx={{
              position: "absolute",
              left: tooltip.x,
              top: tooltip.y,
              bgcolor: "rgba(15,23,42,0.9)",
              color: "#f8fafc",
              borderRadius: 1,
              px: 1.5,
              py: 1,
              fontSize: 12,
              pointerEvents: "none",
              zIndex: 20,
              maxWidth: 220,
              boxShadow: 3,
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              {tooltip.node.name}
            </Typography>
            <Typography variant="caption" display="block">
              Type : {tooltip.node.node_type ?? "—"}
            </Typography>
            <Typography variant="caption" display="block">
              Statut : {tooltip.node.status ?? "inconnu"}
            </Typography>
            {tooltip.node.console_port && (
              <Typography variant="caption" display="block">
                Console : {tooltip.node.console_type} :{tooltip.node.console_port}
              </Typography>
            )}
            <Typography
              variant="caption"
              display="block"
              sx={{ opacity: 0.6, mt: 0.5 }}
            >
              Cliquer pour ouvrir la console
            </Typography>
          </Box>
        )}
      </Box>

      {/* Légende */}
      <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
        {[
          { color: STATUS_FILL.started,   label: "Démarré" },
          { color: STATUS_FILL.stopped,   label: "Arrêté"  },
          { color: STATUS_FILL.suspended, label: "Suspendu"},
          { color: DEFAULT_FILL,          label: "Inconnu" },
        ].map(({ color, label }) => (
          <Stack key={label} direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: color,
                border: `2px solid ${color}`,
              }}
            />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {label}
            </Typography>
          </Stack>
        ))}
        <Typography variant="caption" sx={{ opacity: 0.45, ml: "auto" }}>
          Molette = zoom · Glisser = déplacer · Clic nœud = console
        </Typography>
      </Stack>
    </Paper>
  );
}
